/* Cart drawer auto re-tier — interception path.
 *
 * For peachy subscription line items we intercept trash + qty buttons
 * before Dawn's native cart handler runs. We then do one clear-and-readd
 * at the right tier (selling plan + Loop _bundleId for the new total bag
 * count) and render the drawer once. This avoids the visible "wrong
 * price for the new quantity" flash that you'd otherwise see between the
 * native cart-change response and a follow-up re-tier.
 *
 * Non-subscription lines (one-time bags, anything else) keep their native
 * handlers — qty changes there don't need re-tiering.
 */
/* Storefront error reporter (2026-09-28).
 *
 * window.PeachyReportError(kind, info) sends ONE small report to the
 * private error log (ops-api/, Vercel project peachy-ops-api) when a
 * customer-facing cart action has already failed: a buy-box Add to Cart
 * that showed an error, or a drawer edit that didn't go through. Only
 * ever called on a failure path, so the working path never pays for it.
 * Fire-and-forget via sendBeacon (survives page exit, never blocks);
 * fully wrapped so it can never throw into the caller; capped at 5 per
 * page view. Page path only (no query string) and no personal data.
 * Read the log with ops-api/scripts/read-errors.mjs.
 */
(function () {
  'use strict';
  if (window.PeachyReportError) return;
  var ENDPOINT = 'https://peachy-ops-api.vercel.app/api/client-error';
  var sent = 0;
  window.PeachyReportError = function (kind, info) {
    try {
      if (sent >= 5) return;
      sent++;
      info = info || {};
      var ua = navigator.userAgent || '';
      var S = window.Shopify || {};
      var body = JSON.stringify({
        kind: kind,
        stage: info.stage,
        message: info.message,
        status: info.status,
        box: info.box,
        mode: info.mode,
        bags: info.bags,
        path: window.location.pathname,
        country: S.country,
        currency: S.currency && S.currency.active,
        theme: S.theme && S.theme.id,
        ig: /Instagram/.test(ua),
        fb: /FBAN|FBAV/.test(ua),
        mobile: /Mobi|Android|iPhone|iPad/.test(ua),
        online: navigator.onLine,
        t: new Date().toISOString()
      });
      if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) return;
      if (window.fetch) {
        fetch(ENDPOINT, {
          method: 'POST',
          mode: 'no-cors',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
          body: body
        }).catch(function () {});
      }
    } catch (e) { /* reporting must never affect the page */ }
  };
})();

(function () {
  'use strict';

  var SRC_SUB = 'bundle-sub';
  var DRAWER_SEL = 'cart-drawer';
  // Arm adoption (2026-08-19): also match 'offer-sub' — the retired
  // offer/duo arms' stamp. Those lines have no other live handler, so
  // refusing them left their edits to Dawn's index-based path (which
  // races the gift reconciler and edits the wrong line). Gift-stamped
  // lines (the old arm's free bag) are excluded: they are the gift
  // engine's to manage, never rebuilt as paid bags. Rebuilt lines are
  // re-stamped 'bundle-sub', so legacy carts converge on the current
  // ladder after one edit.
  var LINE_SEL =
    '[data-peachy-src="' + SRC_SUB + '"]:not([data-free-gift]),' +
    '[data-peachy-src="offer-sub"]:not([data-free-gift])';
  var isHandling = false;

  function readBundleConfig() {
    var el = document.querySelector('[data-bundle]:not([data-bundle-arm])');
    if (el) {
      var sellingPlans = (el.dataset.sellingPlans || '').split(',');
      if (sellingPlans.length < 4) return null;
      var maxQty = parseInt(el.dataset.maxQty, 10) || 4;
      var hardCap = parseInt(el.dataset.hardCap, 10) || maxQty;
      if (hardCap < maxQty) hardCap = maxQty;
      // NOTE: no bundleIds here. The Loop _bundleId is NEVER read from a
      // static source — it is freshly minted per edit via window.PeachyLoopMint.
      return { sellingPlans: sellingPlans, bundleNames: (el.dataset.bundleNames || '').split('|'), maxQty: maxQty, hardCap: hardCap };
    }
    // No buy box on this page — fall back to the config the buy box persisted
    // to localStorage (peachy-cart-bundle-sync.js), so the cart is editable on
    // every page. The Loop _bundleId is still minted fresh, never stored.
    var stored = (window.PeachyBundleStore && window.PeachyBundleStore.load) ? window.PeachyBundleStore.load() : null;
    if (stored && stored.sellingPlans && stored.sellingPlans.length >= 4) {
      var sMax = stored.maxQty || 4;
      var sHard = stored.hardCap || sMax;
      if (sHard < sMax) sHard = sMax;
      return { sellingPlans: stored.sellingPlans, bundleNames: stored.bundleNames || [], maxQty: sMax, hardCap: sHard };
    }
    return null;
  }

  function readSubLinesFromDom() {
    var lis = document.querySelectorAll(LINE_SEL);
    // Adoption applies only while the offer/duo arms are retired. If an
    // arm ever goes live again (IntelliGems class or QA param promotes
    // it), its 'offer-sub' lines are its own to manage — rebuilding them
    // here would re-plan them onto the control ladder mid-test.
    var armLive =
      document.documentElement.classList.contains('peachy-duo-active') ||
      document.documentElement.classList.contains('peachy-offer-v2-active');
    var out = [];
    Array.prototype.forEach.call(lis, function (li) {
      if (armLive && li.getAttribute('data-peachy-src') === 'offer-sub') return;
      var key = li.getAttribute('data-line-key');
      var variantId = parseInt(li.getAttribute('data-line-variant-id'), 10);
      var quantity = parseInt(li.getAttribute('data-line-quantity'), 10) || 0;
      if (!key || !variantId) return;
      out.push({ key: key, variantId: variantId, quantity: quantity, el: li });
    });
    return out;
  }

  // Shopify 422-rejects an ENTIRE updates{} batch when any one key is
  // unknown (verified live 2026-08-19) and fetch() resolves on 4xx — so
  // every cart write must check r.ok, or a failed clear silently flows
  // into the following add and doubles lines.
  function cartJson(r) {
    if (!r.ok) {
      var err = new Error('cart request failed: ' + r.status);
      err.status = r.status;
      throw err;
    }
    return r.json();
  }

  function clearKeys(keys) {
    if (!keys.length) return Promise.resolve();
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(cartJson);
  }

  function clearKeysReturningSections(keys) {
    if (!keys.length) return Promise.resolve(null);
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        updates: updates,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    }).then(cartJson);
  }

  function addItemsWithSections(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    }).then(cartJson);
  }

  function renderSections(json) {
    var drawer = document.querySelector(DRAWER_SEL);
    if (drawer && typeof drawer.renderContents === 'function' && json && json.sections) {
      // false = refresh the (already-open) drawer's contents WITHOUT
      // re-opening. These edits happen inside the open drawer; never force it.
      drawer.renderContents(json, false);
    } else if (drawer && typeof drawer.refreshFromServer === 'function') {
      // No section payload (error body, throttled response): repaint from
      // the live cart rather than leaving the drawer showing lines that
      // may no longer exist. cart.js skips our cartUpdate events now, so
      // nothing else repairs the drawer if we don't.
      drawer.refreshFromServer();
    }
    // renderContents replaces inner markup but doesn't update the outer
    // <cart-drawer> element's is-empty class, so without this the drawer
    // keeps its prior class and the empty-state CSS branch never fires.
    if (drawer && json && typeof json.item_count === 'number') {
      drawer.classList.toggle('is-empty', json.item_count === 0);
    }
  }

  function publishCartUpdate() {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS !== 'object') return Promise.resolve();
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Keep the outer drawer's empty-state class honest; the failure
        // path repaints via refreshFromServer, which doesn't touch it.
        var drawer = document.querySelector(DRAWER_SEL);
        if (drawer && cart && typeof cart.item_count === 'number') {
          drawer.classList.toggle('is-empty', cart.item_count === 0);
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'peachy-bundle-action', cartData: cart });
      })
      .catch(function () {});
  }

  function setLineLoading(li, on) {
    if (!li) return;
    if (on) {
      li.setAttribute('aria-busy', 'true');
      li.classList.add('peachy-bundle-line-busy');
    } else {
      li.removeAttribute('aria-busy');
      li.classList.remove('peachy-bundle-line-busy');
    }
  }

  // Edits that arrive while one is still in flight (2026-10-06). They
  // used to be DROPPED, and a typed number stayed on screen though the
  // cart never got it (box showing 99, cart holding 1). Now they queue by
  // FLAVOR (line keys change on every rebuild): taps add up, a typed
  // number or trash replaces what was queued for that flavor. Replayed
  // against the fresh drawer once the current edit finishes; discarded if
  // that edit failed, since the drawer is repainted from the server then
  // and the shopper sees the real cart.
  var queued = []; // [{ variantId, delta, set }] in arrival order

  function queueEdit(targetKey, action, setQty) {
    var line = readSubLinesFromDom().filter(function (l) { return l.key === targetKey; })[0];
    if (!line) return;
    var q = queued.filter(function (x) { return x.variantId === line.variantId; })[0];
    if (!q) {
      q = { variantId: line.variantId, delta: 0, set: null };
      queued.push(q);
    }
    if (action === 'trash') { q.set = 0; q.delta = 0; }
    else if (action === 'set') { q.set = setQty; q.delta = 0; }
    else if (action === 'plus') { if (q.set !== null) q.set += 1; else q.delta += 1; }
    else if (action === 'minus') { if (q.set !== null) q.set = Math.max(0, q.set - 1); else q.delta -= 1; }
  }

  function replayQueued() {
    var q = queued.shift();
    if (!q) return;
    var lines = readSubLinesFromDom().filter(function (l) { return l.variantId === q.variantId; });
    if (!lines.length) { replayQueued(); return; }
    var current = lines.reduce(function (sum, l) { return sum + l.quantity; }, 0);
    var target = q.set !== null ? q.set : current + q.delta;
    if (target < 0) target = 0;
    if (target === current) { replayQueued(); return; }
    applyChange(lines[0].key, target === 0 ? 'trash' : 'set', target);
  }

  async function applyChange(targetKey, action, setQty) {
    if (isHandling) {
      queueEdit(targetKey, action, setQty);
      return;
    }
    var config = readBundleConfig();
    if (!config) return; // No buy-box config available; let native handle.

    var lines = readSubLinesFromDom();
    if (lines.length === 0) return;

    isHandling = true;
    // Where the edit was when it failed, for the error report below.
    var stage = 'start';
    var failed = false;

    var targetLi = lines.reduce(function (acc, l) { return acc || (l.key === targetKey ? l.el : null); }, null);
    setLineLoading(targetLi, true);

    try {
      // Build new variant→qty from the current DOM state, applying the action.
      var qtyByVariant = {};
      lines.forEach(function (l) {
        var newQty = l.quantity;
        if (l.key === targetKey) {
          if (action === 'trash') newQty = 0;
          else if (action === 'minus') newQty = l.quantity - 1;
          else if (action === 'plus') newQty = l.quantity + 1;
          else if (action === 'set') newQty = setQty;
        }
        if (newQty <= 0) return;
        qtyByVariant[l.variantId] = (qtyByVariant[l.variantId] || 0) + newQty;
      });

      var totalBags = 0;
      Object.keys(qtyByVariant).forEach(function (k) { totalBags += qtyByVariant[k]; });

      var existingKeys = lines.map(function (l) { return l.key; });

      // Bundle empty: clear all sub lines, render that response, done.
      if (totalBags === 0) {
        stage = 'clear_all';
        var clearedJson = await clearKeysReturningSections(existingKeys);
        renderSections(clearedJson);
        await publishCartUpdate();
        return;
      }

      // Over the absolute cap (plus-click or a typed quantity): repaint
      // from the server so the input snaps back instead of displaying a
      // quantity the cart doesn't have. DECREASING edits are exempt: a
      // cart can legitimately sit above the cap (built in the US market,
      // then switched to Canada's 3-pouch cap) and must be able to step
      // DOWN toward compliance — blocking those wedges the drawer
      // between "remove bags to check out" and edits that snap back.
      var priorBags = 0;
      lines.forEach(function (l) { priorBags += l.quantity; });
      if (totalBags > config.hardCap && totalBags >= priorBags) {
        var dEl = document.querySelector(DRAWER_SEL);
        if (dEl && typeof dEl.refreshFromServer === 'function') dEl.refreshFromServer();
        return;
      }

      // Quantities above the top discount tier reuse the top tier's
      // selling plan (Loop's bundle is configured as a range, e.g. 4–10, so
      // the same bundle accepts the higher count).
      var idx = Math.min(totalBags, config.maxQty) - 1;
      var sellingPlan = config.sellingPlans[idx] || '';
      var bundleName = totalBags >= 2 ? (config.bundleNames[idx] || '') : '';

      // Mint a FRESH, unique Loop transaction for tier 2+ bundles and stamp
      // its txnId on every rebuilt line. This is the ONLY source of a
      // _bundleId — never a static value. If minting fails we abort the edit
      // entirely (the cart is untouched, since we mint before clearing), so
      // a stale/blank/shared id can never reach the cart.
      // Loop mint unavailable or failed: fall back to a plain key-based
      // edit so the click is never dead (the event is already cancelled).
      // The line keeps its old selling plan -- tier drift is corrected by
      // the next successful rebuild; a working edit at yesterday's tier
      // beats a button that does nothing.
      async function nativeFallback() {
        var target = lines.reduce(function (acc, l) { return acc || (l.key === targetKey ? l : null); }, null);
        if (!target) return;
        var fallbackQty = action === 'trash' ? 0
          : action === 'minus' ? target.quantity - 1
          : action === 'set' ? setQty
          : target.quantity + 1;
        if (fallbackQty < 0) fallbackQty = 0;
        var json = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: targetKey,
            quantity: fallbackQty,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        }).then(cartJson);
        renderSections(json);
        await publishCartUpdate();
      }

      var bundleTxnId = '';
      if (totalBags >= 2) {
        if (!window.PeachyLoopMint || typeof window.PeachyLoopMint.mint !== 'function') {
          console.error('[peachy-bundle-action] PeachyLoopMint unavailable; plain edit fallback.');
          await nativeFallback();
          return;
        }
        var productVariants = Object.keys(qtyByVariant).map(function (vid) {
          return { shopifyId: parseInt(vid, 10), quantity: qtyByVariant[vid] };
        });
        stage = 'mint';
        try {
          bundleTxnId = await window.PeachyLoopMint.mint({
            productVariants: productVariants,
            total: totalBags,
            sellingPlanShopifyId: sellingPlan ? parseInt(sellingPlan, 10) : null
          });
        } catch (mintErr) {
          console.error('[peachy-bundle-action] Loop mint failed; plain edit fallback.', mintErr);
          if (window.PeachyReportError) {
            window.PeachyReportError('drawer_mint', {
              stage: 'mint',
              message: mintErr && mintErr.message,
              bags: totalBags
            });
          }
          await nativeFallback();
          return;
        }
      }

      // Preserve the original DOM line order so flavors don't visibly
      // jump around in the cart after a re-tier. JS's Object.keys() sorts
      // numeric-looking keys ascending, which is what was reordering the
      // lines by variant ID.
      var seen = {};
      var newItems = [];
      lines.forEach(function (l) {
        if (seen[l.variantId]) return;
        var qty = qtyByVariant[l.variantId];
        if (!qty) return;
        seen[l.variantId] = true;
        var item = { id: l.variantId, quantity: qty };
        if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
        var props = { _peachySrc: SRC_SUB };
        if (bundleTxnId) props._bundleId = bundleTxnId;
        if (bundleName) props._bundleName = bundleName;
        item.properties = props;
        newItems.push(item);
      });

      // Clear old sub lines (no sections needed yet), then add new ones with sections.
      // One visible drawer render at the end. The add leg retries ONCE on
      // a 429: the clear has already succeeded at that point, so giving
      // up immediately would leave the customer's bags deleted (observed
      // during throttled QA, 2026-08-19). The catch below + the engine's
      // next sweep still handle a double failure.
      stage = 'clear';
      await clearKeys(existingKeys);
      stage = 'add';
      var addJson;
      try {
        addJson = await addItemsWithSections(newItems);
      } catch (addErr) {
        if (!addErr || addErr.status !== 429) throw addErr;
        await new Promise(function (rs) { setTimeout(rs, 1500); });
        addJson = await addItemsWithSections(newItems);
      }
      renderSections(addJson);
      await publishCartUpdate();
    } catch (err) {
      failed = true;
      console.error('[peachy-bundle-action] failed', err);
      // Show the REAL cart again (2026-10-06). The drawer used to keep its
      // pre-failure lines, whose keys Shopify now rejects (422), so every
      // later tap failed too until a page reload. Also tell the rest of
      // the cart code (gifts, checkout guard) what the cart now holds.
      try {
        var drawerEl = document.querySelector(DRAWER_SEL);
        if (drawerEl && typeof drawerEl.refreshFromServer === 'function') drawerEl.refreshFromServer();
        publishCartUpdate();
      } catch (refreshErr) { /* best effort */ }
      if (window.PeachyReportError) {
        window.PeachyReportError('drawer_edit', {
          stage: stage + ':' + action,
          message: err && err.message,
          status: err && err.status,
          bags: totalBags
        });
      }
    } finally {
      setLineLoading(targetLi, false);
      isHandling = false;
      if (failed) queued = [];
      if (queued.length) window.setTimeout(replayQueued, 0);
    }
  }

  // NOTE: LINE_SEL matches both 'bundle-sub' and legacy 'offer-sub'
  // lines (arm adoption, 2026-08-19) — every rebuild converges the cart
  // onto current 'bundle-sub' stamps and the current tier ladder.
  // Typed quantities: the click interceptors never see a manual edit of
  // the number input — it fires a 'change' event that fell through to
  // Dawn's plain quantity update, changing the quantity but NOT the
  // selling plan, so the line kept the old tier's plan and price
  // (found by Harry, 2026-08-19). Route it through the same re-tier
  // rebuild as the buttons. Gift lines and one-time lines are excluded
  // by LINE_SEL exactly as for clicks.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.classList || !input.classList.contains('quantity__input')) return;
    var li = input.closest('li[data-line-key]');
    if (!li || !li.matches(LINE_SEL)) return;
    if (!readBundleConfig()) return; // no config — let Dawn handle it
    var key = li.getAttribute('data-line-key');
    if (!key) return;
    var typed = parseInt(input.value, 10);
    var current = parseInt(li.getAttribute('data-line-quantity'), 10) || 0;
    if (isNaN(typed) || typed < 0) {
      input.value = current;
      return;
    }
    if (typed === current) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    applyChange(key, 'set', typed);
  }, true);

  document.addEventListener('click', function (e) {
    var li = e.target.closest(LINE_SEL);
    if (!li) return;

    var trashBtn = e.target.closest('cart-remove-button button, cart-remove-button');
    var minusBtn = e.target.closest('button[name="minus"]');
    var plusBtn = e.target.closest('button[name="plus"]');
    if (!trashBtn && !minusBtn && !plusBtn) return;

    var key = li.getAttribute('data-line-key');
    if (!key) return;
    // Bail BEFORE cancelling the event when no bundle config is available
    // (first visit landing on a non-buy-box page, blocked localStorage).
    // With the event cancelled and no config, the click would be silently
    // dead -- let Dawn's native key-based handler take it instead.
    if (!readBundleConfig()) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    var action = trashBtn ? 'trash' : (minusBtn ? 'minus' : 'plus');
    applyChange(key, action);
  }, true);
})();
